import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = {
  priceListName: "Standard",
  effectiveFrom: "",
  effectiveTo: "",
  basePrice: "",
  purchasePrice: "",
  perUnit: "",
  taxRateId: "",
  active: true,
};

const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const PriceLists = () => {
  const [products, setProducts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
  const unitOptions = selectedProduct
    ? [selectedProduct.uom, selectedProduct.boxUom, selectedProduct.caseUom].filter(Boolean)
    : [];

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [productsData, taxRatesData] = await Promise.all([
          api.get("/api/v1/products", { size: 200 }),
          api.get("/api/v1/products/tax-rates"),
        ]);
        setProducts(productsData?.content || []);
        setTaxRates(Array.isArray(taxRatesData) ? taxRatesData : []);
      } catch (error) {
        toast.error(error.message || "Failed to load products / tax rates");
      }
    };
    loadOptions();
  }, []);

  const loadPriceLists = async (productId) => {
    if (!productId) {
      setPriceLists([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api.get("/api/v1/products/price-lists", { productId });
      setPriceLists(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load price lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPriceLists(selectedProductId);
  }, [selectedProductId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductId) {
      toast.error("Select a product first");
      return;
    }
    if (formData.basePrice === "" && formData.purchasePrice === "") {
      toast.error("Enter the sales price or the purchase price");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/v1/products/price-lists", {
        productId: Number(selectedProductId),
        priceListName: formData.priceListName,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo || null,
        basePrice: formData.basePrice === "" ? 0 : Number(formData.basePrice),
        purchasePrice: formData.purchasePrice === "" ? null : Number(formData.purchasePrice),
        perUnit: formData.perUnit || null,
        taxRateId: formData.taxRateId ? Number(formData.taxRateId) : null,
        active: formData.active,
      });
      toast.success("Price list created");
      setShowModal(false);
      setFormData(emptyForm);
      loadPriceLists(selectedProductId);
    } catch (error) {
      toast.error(error.message || "Failed to save price list");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading price lists...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
          <option value="">Select product</option>
          {products.map((product) => (
            <option key={product.id} value={product.id}>
              {product.name} ({product.skuCode})
            </option>
          ))}
        </select>
        <button
          className="primary"
          onClick={() => {
            // A new price starts today; saving it ends the product's previous price the day before.
            setFormData({ ...emptyForm, effectiveFrom: todayIso() });
            setShowModal(true);
          }}
          disabled={!selectedProductId}
        >
          + New Price List
        </button>
      </div>

      {!selectedProductId ? (
        <div className="page-subtitle" style={{ marginTop: "16px" }}>
          Select a product above to view or add its price lists.
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Effective From</th>
                <th>Effective To</th>
                <th>Sales Price</th>
                <th>Purchase Price</th>
                <th>Per Unit</th>
                <th>Tax Rate</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {priceLists.length === 0 ? (
                <tr>
                  <td colSpan={9}>No price lists found for this product.</td>
                </tr>
              ) : (
                priceLists.map((priceList) => (
                  <tr key={priceList.id}>
                    <td>{priceList.id}</td>
                    <td>{priceList.priceListName}</td>
                    <td>{priceList.effectiveFrom}</td>
                    <td>{priceList.effectiveTo || "-"}</td>
                    <td>{Number(priceList.basePrice) > 0 ? priceList.basePrice : "-"}</td>
                    <td>{Number(priceList.purchasePrice) > 0 ? priceList.purchasePrice : "-"}</td>
                    <td>{priceList.perUnit || "-"}</td>
                    <td>{taxRates.find((t) => t.id === priceList.taxRateId)?.name || priceList.taxRateId || "-"}</td>
                    <td>
                      <span className={`badge ${priceList.active ? "badge-green" : "badge-gray"}`}>
                        {priceList.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Create Price List</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Price List Name</label>
                  <input name="priceListName" value={formData.priceListName} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Sales Price</label>
                  <input type="number" step="0.01" min="0" name="basePrice" value={formData.basePrice} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Purchase Price</label>
                  <input type="number" step="0.01" min="0" name="purchasePrice" value={formData.purchasePrice} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Per Unit</label>
                  <select name="perUnit" value={formData.perUnit} onChange={handleChange}>
                    <option value="">Base unit ({selectedProduct?.uom || "default"})</option>
                    {unitOptions.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Effective From</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="effectiveFrom" value={formData.effectiveFrom} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Effective To (optional)</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="effectiveTo" value={formData.effectiveTo} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Tax Rate</label>
                  <select name="taxRateId" value={formData.taxRateId} onChange={handleChange}>
                    <option value="">None</option>
                    {taxRates.map((rate) => (
                      <option key={rate.id} value={rate.id}>
                        {rate.name} ({rate.ratePercentage}%)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Active</label>
                  <select
                    name="active"
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PriceLists;
